export type Ponto = { dia: string; valor: number };

/**
 * Serie temporal em SVG inline.
 *
 * Sem biblioteca de grafico: uma lib pesaria mais que o painel inteiro para
 * desenhar uma linha. Grade em hairline SOLIDO (tracejado le como "projecao"),
 * sem marcador em cada ponto e sem numero em cima de cada um -- so o ultimo
 * ponto leva rotulo direto.
 *
 * A serie TERMINA ONTEM, sempre: o rollup so consolida o dia fechado, e plotar
 * um "hoje" pela metade produziria aquela coluna final baixinha que e a mentira
 * classica de dashboard. O rodape diz isso em texto.
 */
export function Serie(
  { pontos, altura = 140, rotuloValor = "maquinas" }:
  { pontos: Ponto[]; altura?: number; rotuloValor?: string },
) {
  if (pontos.length < 2) {
    return <p className="apagado pequeno">Ainda nao ha historico suficiente para o grafico.</p>;
  }

  const L = 700;
  const A = altura;
  const margem = { topo: 12, dir: 44, baixo: 22, esq: 30 };
  const larguraPlot = L - margem.esq - margem.dir;
  const alturaPlot = A - margem.topo - margem.baixo;

  const maximo = Math.max(1, ...pontos.map((p) => p.valor));
  const x = (i: number) => margem.esq + (i / (pontos.length - 1)) * larguraPlot;
  const y = (v: number) => margem.topo + alturaPlot - (v / maximo) * alturaPlot;

  const linha = pontos.map((p, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(p.valor).toFixed(1)}`).join(" ");
  const area = `${linha} L${x(pontos.length - 1).toFixed(1)},${margem.topo + alturaPlot} L${x(0).toFixed(1)},${margem.topo + alturaPlot} Z`;

  const ultimo = pontos[pontos.length - 1]!;
  const primeiro = pontos[0]!;
  const meio = pontos[Math.floor(pontos.length / 2)]!;
  const dia = (d: string) => d.slice(8, 10) + "/" + d.slice(5, 7);

  return (
    <>
      <svg className="serie" viewBox={`0 0 ${L} ${A}`} role="img"
           aria-label={`${rotuloValor} por dia nos ultimos ${pontos.length} dias`}>
        {[0, 0.5, 1].map((f) => (
          <line key={f} className="serie__grade"
                x1={margem.esq} x2={margem.esq + larguraPlot}
                y1={margem.topo + alturaPlot * f} y2={margem.topo + alturaPlot * f} />
        ))}
        <text className="serie__eixo" x={margem.esq - 6} y={margem.topo + 4} textAnchor="end">{maximo}</text>
        <text className="serie__eixo" x={margem.esq - 6} y={margem.topo + alturaPlot} textAnchor="end">0</text>

        <path className="serie__area" d={area} />
        <path className="serie__linha" d={linha} />

        {/* Rotulo direto so no ultimo ponto. Um numero em cada ponto e caos. */}
        <circle cx={x(pontos.length - 1)} cy={y(ultimo.valor)} r="3" fill="var(--marca-grafico)" />
        <text className="serie__ponta" x={x(pontos.length - 1) + 7} y={y(ultimo.valor) + 4}>
          {ultimo.valor}
        </text>

        <text className="serie__eixo" x={margem.esq} y={A - 6}>{dia(primeiro.dia)}</text>
        <text className="serie__eixo" x={margem.esq + larguraPlot / 2} y={A - 6} textAnchor="middle">{dia(meio.dia)}</text>
        <text className="serie__eixo" x={margem.esq + larguraPlot} y={A - 6} textAnchor="end">{dia(ultimo.dia)}</text>
      </svg>
      <p className="micro apagado mb-0">
        {rotuloValor} por dia · {pontos.length} dias ate ontem ({dia(ultimo.dia)}) ·
        {" "}ultimo dia fechado: {ultimo.valor}
      </p>
    </>
  );
}

/**
 * Faixa de presenca: uma coluna por dia, preenchida quando a maquina reportou.
 *
 * A medida e presenca/ausencia, nao o total de batidas: numa maquina de 6h o
 * total e quase sempre quatro, e desenhar isso como altura fingiria variacao
 * que nao existe. O texto embaixo carrega o numero, para o valor nunca depender
 * so do desenho.
 */
export function FaixaPresenca({ dias }: { dias: { dia: string; total: number }[] }) {
  const presentes = dias.filter((d) => d.total > 0).length;
  return (
    <>
      <div className="presenca" role="img"
           aria-label={`reportou em ${presentes} dos ultimos ${dias.length} dias`}>
        {dias.map((d) => (
          <span key={d.dia}
                className={`presenca__dia${d.total > 0 ? " presenca__dia--sim" : ""}`}
                title={`${d.dia}: ${d.total > 0 ? `${d.total} batidas` : "sem contato"}`} />
        ))}
      </div>
      <p className="micro apagado mt-lg mb-0">
        reportou em {presentes} dos ultimos {dias.length} dias (ate ontem)
      </p>
    </>
  );
}

/**
 * Instancias no pico do dia, com linha de referencia no contratado.
 *
 * Grafico SEPARADO da faixa de presenca de proposito: unidade e escala
 * diferentes. Junta-las num eixo duplo inventaria uma correlacao que nao existe
 * no dado.
 */
export function SerieInstancias(
  { dias, limite }: { dias: { dia: string; instancias_max: number }[]; limite: number },
) {
  if (dias.length < 2) return null;

  const L = 700, A = 120;
  const margem = { topo: 12, dir: 44, baixo: 20, esq: 30 };
  const larguraPlot = L - margem.esq - margem.dir;
  const alturaPlot = A - margem.topo - margem.baixo;
  const maximo = Math.max(1, limite, ...dias.map((d) => d.instancias_max));

  const x = (i: number) => margem.esq + (i / (dias.length - 1)) * larguraPlot;
  const y = (v: number) => margem.topo + alturaPlot - (v / maximo) * alturaPlot;
  const linha = dias.map((d, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(d.instancias_max).toFixed(1)}`).join(" ");
  const pico = Math.max(...dias.map((d) => d.instancias_max));

  return (
    <>
      <svg className="serie" viewBox={`0 0 ${L} ${A}`} role="img"
           aria-label={`pico de instancias por dia, contra o limite de ${limite}`}>
        <line className="serie__referencia" x1={margem.esq} x2={margem.esq + larguraPlot}
              y1={y(limite)} y2={y(limite)} />
        <text className="serie__eixo" x={margem.esq + larguraPlot + 6} y={y(limite) + 4}>
          limite {limite}
        </text>
        <path className="serie__linha" d={linha} />
        <text className="serie__eixo" x={margem.esq - 6} y={margem.topo + 4} textAnchor="end">{maximo}</text>
      </svg>
      <p className="micro apagado mb-0">
        pico de instancias por dia · maior pico no periodo: {pico} de {limite} contratadas
      </p>
    </>
  );
}
